import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Menu, X, Calculator, Users, Phone, ChevronDown, User, LogOut } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/components/AuthProvider";
import logoUrl from "@/assets/natcon-logo.png";

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { user, signOut, loading } = useAuth();

  const navigateToCalculator = () => {
    window.location.href = '/calculator';
  };

  const navigation = [
    { name: "Home", href: "/" },
    { name: "About Us", href: "/about" },
    { name: "Membership", href: "/membership" },
    { name: "Become a Member Now", href: "/membership-application" },
    { name: "Products & Services", href: "/products" },
    
    { name: "News & Media", href: "/news-media" },
    { name: "FAQ", href: "/faq" },
    { name: "Tenders", href: "/tenders" },
    { name: "Careers", href: "/careers" },
    { name: "Contact", href: "/contact" },
  ];

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-20 items-center justify-between">
        {/* Logo */}
        <div className="flex items-center space-x-3 min-w-0 flex-shrink-0">
          <img 
            src={logoUrl}
            alt="Natcon Staff Sacco Logo"
            className="h-12 w-auto flex-shrink-0"
            loading="eager"
            width="48"
            height="48"
          />
          <div className="min-w-0">
            <h1 className="text-lg sm:text-xl font-bold text-foreground leading-tight">Natcon Staff Sacco</h1>
            <p className="text-xs text-muted-foreground whitespace-nowrap">Your Partner in Prosperity</p>
          </div>
        </div>

        {/* Desktop Menu Button */}
        <div className="hidden md:flex items-center space-x-4">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2">
                <Menu className="h-4 w-4" />
                Menu
                <ChevronDown className="h-4 w-4 transition-transform duration-200 group-data-[state=open]:rotate-180" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent 
              align="end" 
              className="w-56 animate-in fade-in-0 zoom-in-95 duration-200"
            >
              {navigation.map((item) => (
                <DropdownMenuItem key={item.name} asChild>
                  <a
                    href={item.href}
                    className="w-full cursor-pointer text-sm font-medium hover:text-primary transition-colors"
                  >
                    {item.name}
                  </a>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          
          <Button variant="outline" size="sm" className="gap-2" onClick={navigateToCalculator}>
            <Calculator className="h-4 w-4" />
            Loan Calculator
          </Button>
          
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2">
                  <User className="h-4 w-4" />
                  Account
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem disabled className="opacity-70">
                  {user.email}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <a href="https://selfservice.natconsacco.or.ke/" target="_blank" rel="noopener noreferrer" className="cursor-pointer">
                    Member Portal
                  </a>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={signOut} className="text-destructive cursor-pointer">
                  <LogOut className="h-4 w-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button size="sm" className="bg-gradient-primary hover:opacity-90" asChild>
              <a href="https://selfservice.natconsacco.or.ke/" target="_blank" rel="noopener noreferrer">
                Sign In
              </a>
            </Button>
          )}
        </div>

        {/* Mobile Menu */}
        <Sheet open={isOpen} onOpenChange={setIsOpen}>
          <SheetTrigger asChild className="md:hidden">
            <Button variant="ghost" size="icon" aria-label="Open mobile menu" className="min-h-[44px] min-w-[44px]">
              <Menu className="h-6 w-6" />
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-[85vw] max-w-sm overflow-y-auto">
            <div className="flex flex-col space-y-6 mt-6">
              <nav className="flex flex-col space-y-2">
                {navigation.map((item) => (
                  <a
                    key={item.name}
                    href={item.href}
                    className="text-base font-medium text-foreground hover:text-primary transition-colors py-3 px-2 rounded-md hover:bg-accent min-h-[44px] flex items-center"
                    onClick={() => setIsOpen(false)}
                  >
                    {item.name}
                  </a>
                ))}
              </nav>
              <div className="flex flex-col space-y-3 pt-6 border-t">
                <Button variant="outline" className="gap-2 justify-start w-full" onClick={() => { navigateToCalculator(); setIsOpen(false); }}>
                  <Calculator className="h-5 w-5" />
                  Loan Calculator
                </Button>
                
                {user ? (
                  <>
                    <Button variant="outline" className="gap-2 justify-start w-full" asChild>
                      <a href="https://selfservice.natconsacco.or.ke/" target="_blank" rel="noopener noreferrer">
                        <User className="h-5 w-5" />
                        Member Portal
                      </a>
                    </Button>
                    <Button variant="outline" className="gap-2 justify-start text-destructive w-full" onClick={() => { signOut(); setIsOpen(false); }}>
                      <LogOut className="h-5 w-5" />
                      Sign Out
                    </Button>
                  </>
                ) : (
                  <Button className="bg-gradient-primary hover:opacity-90 w-full" asChild>
                    <a href="https://selfservice.natconsacco.or.ke/" target="_blank" rel="noopener noreferrer">
                      Sign In
                    </a>
                  </Button>
                )}
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
};

export default Header;